import { TranscribeClient, StartTranscriptionJobCommand, GetTranscriptionJobCommand } from '@aws-sdk/client-transcribe';
import { S3Client, GetObjectCommand } from '@aws-sdk/client-s3';
import { validateConfig } from './config';

let transcribeClient: TranscribeClient | null = null;
let s3Client: S3Client | null = null;

async function getClients() {
  try {
    // Always validate config before creating clients
    const config = await validateConfig();
    
    if (!transcribeClient || !s3Client) {
      console.log('Initializing AWS clients...');
      
      transcribeClient = new TranscribeClient({
        region: config.AWS_REGION,
        credentials: {
          accessKeyId: config.AWS_ACCESS_KEY_ID,
          secretAccessKey: config.AWS_SECRET_ACCESS_KEY,
        }
      });

      s3Client = new S3Client({
        region: config.AWS_REGION,
        credentials: {
          accessKeyId: config.AWS_ACCESS_KEY_ID,
          secretAccessKey: config.AWS_SECRET_ACCESS_KEY,
        }
      });

      console.log('AWS clients initialized successfully');
    }
    
    return { transcribeClient, s3Client };
  } catch (error) {
    console.error('Failed to initialize AWS clients:', error);
    throw error;
  }
}

async function getTranscriptFromS3(bucket: string, key: string): Promise<{ text: string; duration: number }> {
  try {
    console.log('Fetching transcript from S3:', { bucket, key });
    
    const { s3Client } = await getClients();
    const command = new GetObjectCommand({
      Bucket: bucket,
      Key: key
    });

    const response = await s3Client.send(command);
    const str = await response.Body?.transformToString();
    if (!str) {
      throw new Error('Empty transcript file');
    }
    
    const data = JSON.parse(str);
    if (!data.results?.transcripts?.[0]?.transcript) {
      throw new Error('Invalid transcript format');
    }
    
    // Get duration from the results
    const duration = data.results.duration || 0;
    
    console.log('Transcript fetched successfully with duration:', duration);
    return {
      text: data.results.transcripts[0].transcript,
      duration: Math.ceil(duration)
    };
  } catch (error) {
    console.error('Error fetching transcript from S3:', error);
    throw error;
  }
}

export async function transcribeVideo(s3Url: string): Promise<{ text: string; duration: number }> {
  try {
    console.log('Starting video transcription:', { s3Url });
    
    const config = await validateConfig();
    const { transcribeClient, s3Client } = await getClients();

    if (!s3Url.startsWith('s3://')) {
      throw new Error('Invalid S3 URL format');
    }

    const bucketName = config.AWS_S3_BUCKET;
    const region = config.AWS_REGION;
    const key = s3Url.replace(`s3://${bucketName}/`, '');
    const transcriptKey = `transcripts/${key}-transcript.json`;
    
    // Construct the media URL using the virtual-hosted style
    const mediaFileUri = `https://${bucketName}.s3.${region}.amazonaws.com/${key}`;
    const jobName = `transcription-${Date.now()}`;

    // Map file extensions to AWS Transcribe supported formats
    const formatMap: Record<string, string> = {
      'mp4': 'mp4',
      'mov': 'mp4', // Treat MOV as MP4
      'webm': 'webm',
      'm4a': 'm4a',
      'mp3': 'mp3',
      'wav': 'wav',
      'flac': 'flac',
      'ogg': 'ogg',
      'amr': 'amr'
    };

    // Get file extension and map to supported format
    const fileExtension = key.split('.').pop()?.toLowerCase();
    if (!fileExtension || !formatMap[fileExtension]) {
      throw new Error('Unsupported file format. Please use MP4, MOV, WebM, MP3, WAV, FLAC, OGG, AMR, or M4A files.');
    }

    console.log('Starting transcription job:', {
      jobName,
      mediaFileUri,
      outputKey: transcriptKey,
      mediaFormat: formatMap[fileExtension]
    });

    try {
      // Verify the file exists in S3 first
      try {
        await s3Client?.send(new GetObjectCommand({
          Bucket: bucketName,
          Key: key
        }));
      } catch (s3Error: any) {
        if (s3Error.name === 'NoSuchKey') {
          throw new Error('Video file not found in S3. Please try uploading again.');
        }
        throw s3Error;
      }

      const startCommand = new StartTranscriptionJobCommand({
        TranscriptionJobName: jobName,
        Media: { MediaFileUri: mediaFileUri },
        LanguageCode: 'en-US',
        OutputBucketName: bucketName,
        OutputKey: transcriptKey,
        Settings: {
          ShowSpeakerLabels: false // Remove MaxSpeakerLabels since we don't need speaker labels
        },
        MediaFormat: formatMap[fileExtension]
      });

      await transcribeClient.send(startCommand);
      console.log('Transcription job started successfully');
    } catch (error: any) {
      console.error('Error starting transcription job:', error);
      
      if (error.name === 'AccessDeniedException') {
        throw new Error('Access denied. Please check your AWS Transcribe permissions.');
      }
      
      if (error.name === 'BadRequestException') {
        // Log detailed error information
        console.error('BadRequestException details:', {
          message: error.message,
          $metadata: error.$metadata,
          jobName,
          mediaFileUri,
          bucketName,
          key
        });
        
        throw new Error(
          'Invalid request. This could be due to:\n' +
          '- File format not supported\n' +
          '- File is corrupted\n' +
          '- File is too large\n' +
          '- File is not accessible\n' +
          'Please try with a different video file.'
        );
      }
      
      throw error;
    }

    const MAX_POLLING_TIME = 15 * 60 * 1000; // 15 minutes
    const POLLING_INTERVAL = 5000; // 5 seconds
    const START_TIME = Date.now();
    
    console.log('Polling for transcription job completion...');
    
    while (true) {
      if (Date.now() - START_TIME > MAX_POLLING_TIME) {
        throw new Error('Transcription timed out after 15 minutes');
      }

      const { TranscriptionJob } = await transcribeClient.send(
        new GetTranscriptionJobCommand({ TranscriptionJobName: jobName })
      );

      if (!TranscriptionJob) {
        throw new Error('Transcription job not found');
      }

      console.log('Job status:', TranscriptionJob.TranscriptionJobStatus);

      if (TranscriptionJob.TranscriptionJobStatus === 'COMPLETED') {
        console.log('Transcription job completed successfully');
        try {
          return await getTranscriptFromS3(bucketName, transcriptKey);
        } catch (error) {
          console.error('Error reading transcript from S3:', error);
          throw new Error('Failed to read transcript from S3. Please check bucket permissions.');
        }
      }

      if (TranscriptionJob.TranscriptionJobStatus === 'FAILED') {
        const error = TranscriptionJob.FailureReason || 'Transcription failed';
        console.error('Transcription job failed:', error);
        throw new Error(error);
      }

      await new Promise(resolve => setTimeout(resolve, POLLING_INTERVAL));
    }
  } catch (error) {
    console.error('Transcription error:', error);
    throw error instanceof Error ? error : new Error('Unknown transcription error');
  }
}